import React, { useState } from 'react';
import { navigate } from '@reach/router';
import { useDispatch } from 'react-redux';
import {
  applyForJob,
  postExpressInterest,
} from 'components/pages/jobBoard/JobBoardApi';
import { Spinner } from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import { setOpenSignUpForm } from 'reducers/landingPage';
import {
  setApplicationType,
  setIsStagesOnlyApplicationForm,
  setProjectId,
} from 'reducers/virtualAssessments';
import MatchRank from 'components/CardViewComp/MatchRank/MatchRank';
import RateIcon from 'assets/apply.clevry/Icon_Card_01_DarkBlue.png';
import ScoreBarChart from 'components/CardViewComp/ScoreBarChart/ScoreBarChart';
import { openModal } from 'reducers/modal';
import { Toast } from 'components/toastMessages/toast';
import { useSelector } from 'react-redux';
import { setBlockedInterest } from 'reducers/matchingProcess';
import { selectDashboardInfo } from 'reducers/dashboardInfo';
import { AppPath } from 'appConstants';
import { selectInIframe } from 'reducers/auth';
import { windowWhen } from 'rxjs';
import { isStaging, isUAT } from 'config';

export interface JobBoardCardData {
  listing__ProjectId: number | null;
  listing__HiringManagerSearchId: number | null;
  listing__JobApplicationId: number | null;
  listing__AssessmentId: number | null;
  listing__Title: string | null;
  listing__ClientName: string | null;
  listing__JobType: string | null;
  listing__WorkSchedules: string | null;
  listing__Location: string | null;
  listing__CurrencyDisplayCode: string | null;
  listing__CurrencySymbol: string | null;
  listing__SalaryMin: number | null;
  listing__SalaryMax: number | null;
  listing__Salary: string | null;
  listing__ShortDescription: string | null;
  listing__LogoURL: string | null;
  listing__JobApplicationIsApplicationComplete: boolean | null;
  listing__HiringManagerSearchTotalMatchPercentage: number;
  listing__HiringManagerSearchSoftSkillsMatchPercentage: number;
  listing__HiringManagerSearchHardSkillsMatchPercentage: number;
  listing__HiringManagerSearchCandidateInterestId: number | null;
  listing__HiringManagerSearchCandidateInterestHiringManagerInterest:
    | boolean
    | null;
  listing__HiringManagerSearchCandidateInterestCandidateInterest:
    | boolean
    | null;
  listing__Type: string | null;
  listing__IsStagesOnlyApplicationFlow: boolean | null;
}

interface JobBoardCardProps {
  data: JobBoardCardData;
  setToggleApplyNowPopup?: React.Dispatch<React.SetStateAction<boolean | null>>;
  setSelectedJobData?: React.Dispatch<
    React.SetStateAction<JobBoardCardData | null>
  >;
  hasCompletedMatchingProcess: boolean;
  isPublic?: boolean;
  currentPage?: number;
}
const JobBoardCard: React.FC<JobBoardCardProps> = ({
  data,
  setToggleApplyNowPopup,
  setSelectedJobData,
  hasCompletedMatchingProcess,
  isPublic,
  currentPage,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const dashboardInfo = useSelector(selectDashboardInfo);
  const insideIframe = useSelector(selectInIframe);
  let lastMatchingPageVisited: string = '?job-opportunities';
  if (dashboardInfo && dashboardInfo.lastMatchingPageVisited) {
    lastMatchingPageVisited = dashboardInfo.lastMatchingPageVisited;
  }

  const { t } = useTranslation('Job+Board', { i18n });

  const applyHandler = async (id: number) => {
    console.log('applyHandler JobBoardCard');
    const result: any = await applyForJob({ ProjectId: id });

    console.log('applyHandler result: ');

    if (result && result.response && result.response.status === '1') {
      setIsLoading(false);

      const applicationFormId = result.response.id;

      if (
        result?.response?.details?.isStagesOnlyApplicationFlow &&
        result?.response?.details?.assessmentID
      ) {
        navigate(
          `/view-application/${result?.response?.details?.assessmentID}/live/stages`,
          {
            state: { projectId: id },
          }
        );
      } else {
        navigate('/job-application', {
          state: { applicationFormId, projectId: id },
        });
      }
    } else if (result && result.errors) {
      let withdrawn = false;

      if (Array.isArray(result.errors)) {
        result.errors.map((item: any) => {
          if (
            typeof item === 'object' &&
            item.hasOwnProperty('fieldName') &&
            item.fieldName === 'Already_SignedUp'
          ) {
            withdrawn = true;
          }
        });
      }

      if (withdrawn) {
        setErrorMessages([
          'You cannot apply to this project as you have withdrawn from it.',
        ]);
      } else {
        setErrorMessages([
          t('SSCandidate_Job_Board_Job_Board_Application_Fail_Load'),
        ]);
      }

      setIsLoading(false);
    } else {
      setIsLoading(false);

      setErrorMessages([
        t('SSCandidate_Job_Board_Job_Board_Application_Fail_Load'),
      ]);
    }
  };

  const handleBlockInterest = async () => {
    const result = await postExpressInterest({
      hiringManagerSearchId: data.listing__HiringManagerSearchId
        ? data.listing__HiringManagerSearchId
        : 0,
      isCandidateInterest: false,
      consentToShareData: false,
    });

    if (result.response && result.response.status === '1') {
      Toast.openToastSuccessMessage(
        t('SSCandidate_Job_Board_Block_Interest_Success')
      );
      dispatch(setBlockedInterest(true));
    }
    if (result.errors) {
      Toast.openToastErrorMessage(
        t('SSCandidate_Job_Board_Block_Interest_Error')
      );
    }
  };

  const handleIframeApplyRedirect = (
    listingId: number | null,
    listingType: string | null
  ) => {
    let regularDomainURL = 'https://joy.clevry.com';
    const clientName = localStorage.getItem('jobBoardClientName');

    // Staging environment leads to UAT domain as iframe testing is being done on "staging-iframetest.clevry.com"
    if (isUAT || isStaging) {
      regularDomainURL =
        'https://criterion-psycruit-findyourjoy-v1.uat.avamae.co.uk';
    }

    const targetURL =
      regularDomainURL +
      `/public-${
        listingType === 'Project' ? 'job' : 'opportunity'
      }-description?id=${listingId}&${
        clientName ? `clientName=${clientName}` : ''
      }`;

    if (listingId && listingType) {
      // Open up new tab to non client-specific version of FYJ job description page and posts message containing the client name
      window.open(targetURL, '_blank');
    }
  };

  const dispatch = useDispatch();

  const interestExpressedByCandidate =
    data.listing__HiringManagerSearchCandidateInterestCandidateInterest;
  const interestExpressedByHiringManager =
    data.listing__HiringManagerSearchCandidateInterestHiringManagerInterest;
  const canExpressInterest =
    data.listing__Type === 'HiringManagerSearch' &&
    !data.listing__HiringManagerSearchCandidateInterestCandidateInterest &&
    !data.listing__HiringManagerSearchCandidateInterestHiringManagerInterest;
  const matchBlocked =
    data.listing__HiringManagerSearchCandidateInterestId &&
    (data.listing__HiringManagerSearchCandidateInterestCandidateInterest ===
      false ||
      data.listing__HiringManagerSearchCandidateInterestHiringManagerInterest ===
        false);

  if (isLoading) {
    return <Spinner />;
  }

  const viewDetailsRedirect = () => {
    const navigateURL =
      data.listing__Type === 'HiringManagerSearch'
        ? 'opportunity-description'
        : 'job-description';
    const descriptionId =
      data.listing__Type === 'HiringManagerSearch'
        ? data.listing__HiringManagerSearchId
        : data.listing__ProjectId;
    if (isPublic) {
      navigate(`/public-${navigateURL}/${descriptionId}`, {
        state: {
          id: data.listing__ProjectId,
          projectId: data.listing__ProjectId,
          matchId: data.listing__HiringManagerSearchId,
          jobApplication__Id: data.listing__JobApplicationId,
          jobApplication__IsApplicationComplete:
            data.listing__JobApplicationIsApplicationComplete,
          assessment__Id: data.listing__AssessmentId,
          listingType: data.listing__Type,
          isPublic: isPublic,
          currentPage: currentPage,
        },
      });
    } else {
      if (
        typeof data === 'object' &&
        data.hasOwnProperty('listing__ProjectId') &&
        data.hasOwnProperty('listing__JobApplicationId') &&
        data.hasOwnProperty('listing__JobApplicationIsApplicationComplete') &&
        data.hasOwnProperty('listing__AssessmentId')
      ) {
        navigate(`/${navigateURL}/${descriptionId}`, {
          state: {
            id: data.listing__ProjectId,
            projectId: data.listing__ProjectId,
            matchId: data.listing__HiringManagerSearchId,
            jobApplication__Id: data.listing__JobApplicationId,
            jobApplication__IsApplicationComplete:
              data.listing__JobApplicationIsApplicationComplete,
            assessment__Id: data.listing__AssessmentId,
            listingType: data.listing__Type,
            isPublic: isPublic,
            currentPage: currentPage,
          },
        });
      }
    }
  };

  return (
    <div className="JobBoardCard">
      <div className="Inner">
        <div className="CandidateDetailsRow">
          <div className="ProfileRow">
            <div
              className={
                'ProfilePic' + (data.listing__LogoURL ? ' WithImage' : '')
              }
            >
              {data.listing__LogoURL ? (
                <img
                  src={data.listing__LogoURL}
                  alt={data.listing__Title?.substring(0, 1).toLocaleUpperCase()}
                  // alt={'Avatar - ' + data.listing__Title}
                />
              ) : (
                data.listing__Title?.substring(0, 1).toLocaleUpperCase()
              )}
            </div>

            <div
              className={`JobInfo ${
                !data.listing__HiringManagerSearchTotalMatchPercentage
                  ? 'Regular'
                  : ''
              }`}
            >
              <div
                className="JobInfo_name"
                onClick={() => viewDetailsRedirect()}
              >
                {data.listing__Title}
              </div>
              <div className="JobInfo_additional">
                {data.listing__ClientName}
              </div>
              <div className="JobInfo_additional">
                {data.listing__JobType && data.listing__JobType}
                {data.listing__JobType && data.listing__WorkSchedules
                  ? `, `
                  : ''}
                {data.listing__WorkSchedules && data.listing__WorkSchedules}
              </div>
              <div className="JobInfo_additional">
                {!data.listing__Location || data.listing__Location === 'null'
                  ? ''
                  : data.listing__Location}
              </div>
            </div>

            {data.listing__Type === 'HiringManagerSearch' &&
              data.listing__HiringManagerSearchTotalMatchPercentage !==
                undefined &&
              !isPublic && (
                <div className="MatchContainer">
                  <div className="JobMatchIndicator">
                    {t('SSCandidate_Job_Board_Job_Match')}
                  </div>
                  <div className="MatchRank">
                    <MatchRank
                      percentage={
                        data.listing__HiringManagerSearchTotalMatchPercentage
                          ? data.listing__HiringManagerSearchTotalMatchPercentage
                          : 0
                      }
                      // overrideText={
                      //   data.listing__HiringManagerSearchTotalMatchPercentage
                      //     ? 'No dat'
                      //     : undefined
                      // }
                      radius={30}
                      strokeWidth={5}
                      strokeFill={'transparent'}
                      fontSize={1.5}
                      fill={'#F5F5F5'}
                    />
                    <div className="Label">
                      {t('SSCandidate_Job_Board_Total_Match')}
                    </div>
                  </div>
                </div>
              )}
          </div>

          <div className="InfoRow">
            <div className="InfoRow_Icon">
              <img
                src={RateIcon}
                alt={`${data.listing__Salary} Icon`}
                width={32}
                height={32}
              />
            </div>
            <div className="InfoRow_Info">
              <p>{t('SSCandidate_Job_Board_Salary_Range')}</p>
              <h4>
                {data.listing__Salary
                  ? data.listing__Salary
                  : t('SSCandidate_Job_Board_Salary_Not_Listed')}
              </h4>
            </div>
          </div>

          {data.listing__Type === 'HiringManagerSearch' &&
            (data.listing__HiringManagerSearchSoftSkillsMatchPercentage !==
              undefined ||
              data.listing__HiringManagerSearchHardSkillsMatchPercentage !==
                undefined) && (
              <div className="RatingsRow">
                <ScoreBarChart
                  chartData={[
                    {
                      label: 'Soft Skills Match',
                      value:
                        data.listing__HiringManagerSearchSoftSkillsMatchPercentage /
                        10,
                    },
                    {
                      label: 'Hard Skills Match',
                      value:
                        data.listing__HiringManagerSearchHardSkillsMatchPercentage /
                        10,
                    },
                  ]}
                />
              </div>
            )}

          <div className="BtmContent">
            <div className="DescriptionRow">
              <p>
                {!data.listing__ShortDescription ||
                data.listing__ShortDescription === 'null'
                  ? ''
                  : data.listing__ShortDescription}
              </p>
            </div>
          </div>
        </div>

        <div className="EndOfRowButtonContainer Card">
          <button className="Btn Btn_02" onClick={() => viewDetailsRedirect()}>
            {t('SSCandidate_Job_Board_View_Details')}
          </button>

          {data.listing__Type === 'HiringManagerSearch' &&
          interestExpressedByCandidate &&
          !matchBlocked ? (
            <div className="EndOfRowButton Btn InterestExpressed Inert">
              {t('SSCandidate_Job_Board_Interest_Expressed')}
            </div>
          ) : data.listing__Type === 'HiringManagerSearch' &&
            canExpressInterest &&
            !matchBlocked ? (
            <button
              className="Btn Btn_01"
              onClick={() => {
                if (insideIframe) {
                  handleIframeApplyRedirect(
                    data.listing__HiringManagerSearchId,
                    data.listing__Type
                  );
                  return;
                }
                if (isPublic) {
                  dispatch(setApplicationType('PUBLIC_EXPRESS_INTEREST'));
                  dispatch(setOpenSignUpForm());
                  return;
                } else {
                  if (!hasCompletedMatchingProcess) {
                    navigate(
                      `${AppPath.MATCHING_PROCESS}${lastMatchingPageVisited}`
                    );
                  } else {
                    dispatch(
                      openModal({
                        modalType: 'EXPRESS_INTEREST',
                        data: {
                          title: t('SSCandidate_Job_Board_Express_Interest'),
                          modalClasses: 'ExpressInterestModal',
                          modalHeaderClasses: 'ExpressInterestModalHeader',
                          searchID: data.listing__HiringManagerSearchId,
                        },
                      })
                    );
                  }
                }
              }}
            >
              {t('SSCandidate_Job_Board_Express_Interest')}
            </button>
          ) : data.listing__Type === 'HiringManagerSearch' &&
            interestExpressedByHiringManager &&
            !matchBlocked ? (
            <div className="SideBySideButtons">
              <button
                className="EndOfRowButton Btn InterestBlockOption"
                onClick={() => handleBlockInterest()}
              >
                {t('SSCandidate_Job_Board_Block')}
              </button>
              <button
                className="EndOfRowButton Btn InterestAcceptOption"
                onClick={() => {
                  if (isPublic) {
                    dispatch(setApplicationType('PUBLIC_EXPRESS_INTEREST'));
                    dispatch(setOpenSignUpForm());
                    return;
                  } else {
                    dispatch(
                      openModal({
                        modalType: 'EXPRESS_INTEREST',
                        data: {
                          title: t(
                            'SSCandidate_Job_Board_Accept_Hiring_Manager_Interest'
                          ),
                          modalClasses: 'ExpressInterestModal',
                          modalHeaderClasses: 'ExpressInterestModalHeader',
                          searchID: data.listing__HiringManagerSearchId,
                        },
                      })
                    );
                  }
                }}
              >
                {t('SSCandidate_Job_Board_Accept')}
              </button>
            </div>
          ) : matchBlocked ? (
            <div className="Btn InterestBlocked EndOfRowButton Inert">
              {t('SSCandidate_Job_Board_Match_Declined')}
            </div>
          ) : data.listing__Type === 'Project' &&
            data.listing__JobApplicationId !== null &&
            data.listing__JobApplicationId !== undefined ? (
            <button
              className="Btn Btn_01"
              onClick={() => {
                if (
                  !data.listing__JobApplicationIsApplicationComplete &&
                  !data.listing__IsStagesOnlyApplicationFlow
                ) {
                  navigate(`/job-application`, {
                    state: {
                      applicationFormId: data.listing__JobApplicationId,
                      projectId: data.listing__ProjectId,
                    },
                  });
                } else {
                  navigate(
                    `/view-application/${data.listing__AssessmentId}/live/stages`,
                    {
                      state: { projectId: data.listing__ProjectId },
                    }
                  );
                }
              }}
            >
              {data.listing__JobApplicationIsApplicationComplete
                ? t('SSCandidate_Job_Board_View_Application')
                : t('SSCandidate_Job_Board_Continue')}
            </button>
          ) : (
            <div
              className={`Btn BtnPrimary ${isLoading ? 'Disabled' : ''}`}
              onClick={() => {
                console.log('JobBoardCard data: ', data);
                if (isLoading) return;
                if (insideIframe) {
                  handleIframeApplyRedirect(
                    data.listing__ProjectId,
                    data.listing__Type
                  );
                  return;
                }
                if (isPublic) {
                  if (data.listing__ProjectId) {
                    dispatch(setProjectId(data.listing__ProjectId));
                    dispatch(setApplicationType('PUBLIC_JOB_APPLICATION'));
                  }
                  console.log('JobBoardCard setOpenSignUpForm');

                  dispatch(
                    setIsStagesOnlyApplicationForm(
                      data?.listing__IsStagesOnlyApplicationFlow ?? false
                    )
                  );

                  dispatch(setOpenSignUpForm());
                  return;
                }
                if (setSelectedJobData) {
                  setSelectedJobData(data);
                }
                console.log('JobBoardCard applyHandler');
                applyHandler(
                  data.listing__ProjectId ? data.listing__ProjectId : 0
                );
                setIsLoading(true);
                if (setToggleApplyNowPopup) setToggleApplyNowPopup(true);
              }}
            >
              {t('SSCandidate_Job_Board_Apply_Now')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { JobBoardCard };
